import type { comluxgroup } from '@luxuryescapes/contract-data-event-schemas'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useInView } from 'react-intersection-observer'
import { useIntl, defineMessages } from 'react-intl'
import { rem } from 'polished'
import styled from 'styled-components'

import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import Clickable from 'components/Common/Clickable'
import Group from 'components/utils/Group'
import IconButton from 'LuxKit/Button/IconButton'
import Image from 'components/Common/Image/Image'
import LineAngleRightIcon from 'LuxKit/Icons/line/LineAngleRightIcon'
import TextButton from 'LuxKit/Button/TextButton'
import { ChertoExperiments } from 'constants/experiments'
import { queryKeyChains, queryKeyPages, queryKeyPropertyTypes, queryKeySubChains } from 'constants/url'
import useExperiment from 'hooks/Experiments/useExperiment'
import { encodeSearchParams } from 'lib/search/searchUtils'
import { setManySearchParamValues } from 'lib/url/searchUrlUtils'
import { REBRAND_PALETTE_CLASS } from 'lib/theme/paletteVersion'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { trackHomesCrossSellBannerClick, trackHomesCrossSellBannerImpression } from 'analytics/snowplow/events/homesCrossSellBannerEvents'
import { HOMES_CROSS_SELL_PROPERTY_TYPES } from './homesCrossSellBannerUtils'

const HOMES_AND_VILLAS_SEARCH_PATH = '/search/homes-and-villas'
const HOTEL_SEARCH_PATH = '/search'

const PLACEHOLDER_THUMBNAIL_IMAGE_ID = '781a4b87-96d1-4c7b-9e5c-8c8ff78d52e8'

const messages = defineMessages({
  urbanHeading: {
    id: 'gBv5p_kbmqV21VZ-FZdc',
    defaultMessage: 'Prefer your own place?',
    description: 'Heading on the homes cross-sell banner shown in hotel search results, for destinations where apartment-style stays dominate available Homes & Villas inventory',
  },
  urbanDescription: {
    id: 'QX2hGtAaeqSyskBj2gyq',
    defaultMessage: 'Explore apartments and homes with more space to spend your trip.',
    description: 'Supporting copy on the homes cross-sell banner shown in hotel search results, for destinations where apartment-style stays dominate available Homes & Villas inventory',
  },
  leisureHeading: {
    id: 'XvtyP7x3bnrcfLL-ay6m',
    defaultMessage: 'Looking for a place all to yourself?',
    description: 'Heading on the homes cross-sell banner shown in hotel search results, for destinations where private homes and villas dominate available Homes & Villas inventory',
  },
  leisureDescription: {
    id: 'YzYmgTnCNOxxxeelj53A',
    defaultMessage: 'Explore private homes and villas for your next getaway.',
    description: 'Supporting copy on the homes cross-sell banner shown in hotel search results, for destinations where private homes and villas dominate available Homes & Villas inventory',
  },
  cta: {
    id: 'ZpOLw0L0whfzlfvR3row',
    defaultMessage: 'See offers',
    description: 'Primary action label on the homes cross-sell banner shown in hotel search results, linking searchers to Homes & Villas inventory',
  },
})

const ThumbnailFrame = styled.div`
  width: ${rem(40)};
  height: ${rem(40)};
  flex-shrink: 0;
`

const Thumbnail = styled(Image)`
  width: 100%;
  height: 100%;
  border-radius: ${props => props.theme.borderRadius.M};
`

const ClickableRow = styled(Clickable)`
  width: 100%;
`

const BannerSheet = styled.div`
  background-color: var(--palette-neutral-surface-light-background);
  color: var(--palette-neutral-surface-light-contrast);
  padding: ${rem(20)};

  body.${REBRAND_PALETTE_CLASS} & {
    border-radius: ${props => props.theme.borderRadius.M};
  }
`

const TextContent = styled.div`
  flex: 1 1 auto;
  min-width: 0;
`

const CtaChevron = styled(IconButton)`
  flex-shrink: 0;

  ${mediaQueryUp.tablet} {
    display: none;
  }
`

const CtaButton = styled(TextButton)`
  display: none;
  flex-shrink: 0;

  ${mediaQueryUp.tablet} {
    display: flex;
  }
`

interface Props {
  variant: 'urban' | 'leisure'
  searchContext: comluxgroup.SearchContext_1_1_1
  windowSearch: string
  searchItem: App.SearchItem | undefined
  checkIn: string | undefined
  checkOut: string | undefined
  rooms: Array<App.Occupants> | undefined
}

function HomesCrossSellBanner({ variant, searchContext, windowSearch, searchItem, checkIn, checkOut, rooms }: Props) {
  const intl = useIntl()
  const hasFiredImpressionRef = useRef(false)
  const [inViewRef, inView] = useInView({
    triggerOnce: true,
    threshold: 0.5,
  })

  useEffect(() => {
    if (inView && !hasFiredImpressionRef.current) {
      hasFiredImpressionRef.current = true
      trackHomesCrossSellBannerImpression(variant, searchContext)
    }
  }, [inView, variant, searchContext])

  const homepageURL = useMemo(() => {
    const params = encodeSearchParams({
      searchItem,
      dates: { checkIn, checkOut },
      rooms,
    })
    return `${HOMES_AND_VILLAS_SEARCH_PATH}?${params.toString()}`
  }, [searchItem, checkIn, checkOut, rooms])

  const filteredSearchURL = useMemo(() => {
    const nextSearch = setManySearchParamValues(windowSearch, [
      { paramName: queryKeyPropertyTypes, paramValue: HOMES_CROSS_SELL_PROPERTY_TYPES.join(',') },
      { paramName: queryKeyPages, paramValue: undefined },
      { paramName: 'limit', paramValue: undefined },
      { paramName: queryKeyChains, paramValue: undefined },
      { paramName: queryKeySubChains, paramValue: undefined },
    ])
    return `${HOTEL_SEARCH_PATH}?${nextSearch}`
  }, [windowSearch])

  // control = open the current search filtered to Homes & Villas/Apartments in a new tab,
  // variant = open the Homes & Villas search homepage with the same search in a new tab.
  const { isAnyVariant: isHomepageTrigger } = useExperiment(
    ChertoExperiments.homesCrossSellBannerTrigger,
    { canActivate: inView },
  )
  const to = isHomepageTrigger ? homepageURL : filteredSearchURL
  const target = '_blank'

  const handleClick = useCallback(() => {
    trackHomesCrossSellBannerClick(variant, searchContext)
  }, [variant, searchContext])

  const heading = intl.formatMessage(variant === 'urban' ? messages.urbanHeading : messages.leisureHeading)
  const description = intl.formatMessage(variant === 'urban' ? messages.urbanDescription : messages.leisureDescription)
  const ctaLabel = intl.formatMessage(messages.cta)

  return (
    <ClickableRow ref={inViewRef} className="flush-banner" data-testid="homes-cross-sell-banner" to={to} target={target} onClick={handleClick}>
      <BannerSheet>
        <Group direction="horizontal" gap={12} verticalAlign="center">
          <ThumbnailFrame>
            <Thumbnail id={PLACEHOLDER_THUMBNAIL_IMAGE_ID} alt="" width={40} height={40} fit="center" objectFit="cover" />
          </ThumbnailFrame>
          <TextContent>
            <BodyTextBlock variant="medium">
              <Group direction="vertical" gap={2}>
                <b>{heading}</b>
                <div>{description}</div>
              </Group>
            </BodyTextBlock>
          </TextContent>
          <CtaChevron kind="tertiary" nonInteractable size="xsmall">
            <LineAngleRightIcon />
          </CtaChevron>
          <CtaButton kind="primary" nonInteractable size="medium" shape="round">
            {ctaLabel}
          </CtaButton>
        </Group>
      </BannerSheet>
    </ClickableRow>
  )
}

export default HomesCrossSellBanner
